import { GrayscalePalette } from '@tcl-boron-colors/colors';
import { Pagination, PaginationValue } from '@tcl-boron-prefabs/pagination';
import { Table as TanstackTable } from '@tanstack/react-table';
import { useMemo } from 'react';

import { useTranslation } from '~/i18n';

import { intToPageSizeOption } from './tablePaginationUtils';

const FilterableDataTablePagination = <D extends object>({
  hidePagination,
  manualPagination,
  onPageChange,
  onPageSizeChange,
  table,
}: {
  hidePagination?: boolean;
  manualPagination?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  table: TanstackTable<D>;
}) => {
  const { pageIndex, pageSize } = table.getState().pagination;
  const t = useTranslation();
  const toPageSizeOption = (size: number) =>
    intToPageSizeOption(size, t('tables.chrome.pageSizeRows', { count: size }));

  const paginationValue = useMemo(
    () => ({
      page: pageIndex + 1,
      pageSize: intToPageSizeOption(pageSize, t('tables.chrome.pageSizeRows', { count: pageSize })),
    }),
    [pageIndex, pageSize, t],
  );

  const onChangePagination = ({ page, pageSize: ps }: PaginationValue) => {
    const size = parseInt(ps.value, 10);
    if (manualPagination) {
      onPageChange(page);
      onPageSizeChange?.(size);
      return;
    }
    table.setPageSize(size);
    table.setPageIndex(page - 1);
  };

  const goToNextPage = () => {
    if (manualPagination) {
      onPageChange(paginationValue.page + 1);
      return;
    }
    table.nextPage();
  };

  const goToPreviousPage = () => {
    if (manualPagination) {
      onPageChange(paginationValue.page - 1);
      return;
    }
    table.previousPage();
  };

  if (hidePagination) {
    return null;
  }

  return (
    <div style={{ paddingRight: 16, backgroundColor: GrayscalePalette[100] }}>
      <Pagination
        goToNextPage={table.getCanNextPage() ? goToNextPage : undefined}
        goToPreviousPage={table.getCanPreviousPage() ? goToPreviousPage : undefined}
        onChange={onChangePagination}
        pageSizeOptions={[15, 30, 50, 100].map(toPageSizeOption)}
        totalPages={table.getPageCount()}
        value={paginationValue}
      />
    </div>
  );
};

export default FilterableDataTablePagination;
